import { QuestionDifficulty } from "@/enums/contentTypes";
import { ActionIcon, Box, Collapse, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ArrowDownwardRounded, ArrowUpwardRounded } from "@mui/icons-material";
import { Question } from "@tsTypes/question-schema";
import { CustomTest, MockExam, Test } from "@tsTypes/test-schema";
import { useMemo } from "react";
import QuestionDifficultyChip from "../../../questions/[topic]/components/Header/QuestionDifficultyChip";
import classes from "./TestPerformanceBreakdown.module.css";

interface DifficultyPerformance {
  difficulty: QuestionDifficulty;
  marksGained: number;
  total: number;
}

interface SubtopicPerformance {
  subtopic: string;
  marksGained: number;
  total: number;
}

interface TestPerformanceBreakdownProps {
  testMeta: CustomTest | MockExam | Test;
  testMarks: { [questionId: string]: number | undefined };
  subjectNameLower: string;
}

export default function TestPerformanceBreakdown({
  testMeta,
  testMarks,
}: TestPerformanceBreakdownProps) {
  const [difficultyOpened, { toggle: toggleDifficulty }] = useDisclosure(false);
  const [subtopicOpened, { toggle: toggleSubtopic }] = useDisclosure(false);

  const subtopicPerformance = useMemo(() => {
    const testSubTopicIds = Object.keys(testMeta.RelatedSubTopics);
    return testMeta.RelatedQuestions.reduce(
      (acc: { [key: string]: SubtopicPerformance }, question: Question) => {
        const relevantSubtopic = question.RelatedSubTopics.find((s) =>
          testSubTopicIds.includes(s.documentId)
        )!.SubTopicTitle;
        if (!acc[relevantSubtopic]) {
          acc[relevantSubtopic] = {
            subtopic: relevantSubtopic,
            marksGained: 0,
            total: 0,
          };
        }
        acc[relevantSubtopic].total += question.TotalMarks;
        acc[relevantSubtopic].marksGained += testMarks[question.documentId]!;
        return acc;
      },
      {}
    );
  }, [testMeta.RelatedQuestions, testMeta.RelatedSubTopics, testMarks]);

  const difficultyPerformance = useMemo(() => {
    return testMeta.RelatedQuestions.reduce(
      (acc: { [key: string]: DifficultyPerformance }, question: Question) => {
        if (!acc[question.Difficulty]) {
          acc[question.Difficulty] = {
            difficulty: question.Difficulty,
            marksGained: 0,
            total: 0,
          };
        }
        acc[question.Difficulty].total += question.TotalMarks;
        acc[question.Difficulty].marksGained += testMarks[question.documentId]!;
        return acc;
      },
      {}
    );
  }, [testMeta.RelatedQuestions, testMarks]);

  return (
    <div className={classes.breakdownWrapper}>
      <Box
        className={classes.breakdownComponentWrapper}
        onClick={toggleDifficulty}
      >
        <Title order={6} className={classes.title}>
          By Difficulty
        </Title>
        <CollapseIcon isOpened={difficultyOpened} />
        <Collapse in={difficultyOpened} className={classes.collapseWrapper}>
          {Object.values(difficultyPerformance).map(
            (perf: DifficultyPerformance) => (
              <Box
                key={perf.difficulty}
                className={classes.collapseItemWrapper}
              >
                <QuestionDifficultyChip difficulty={perf.difficulty} />
                <Text className={classes.marks}>
                  {perf.marksGained} <span>/{perf.total} marks</span>
                </Text>
              </Box>
            )
          )}
        </Collapse>
      </Box>
      <Box
        className={classes.breakdownComponentWrapper}
        onClick={toggleSubtopic}
      >
        <Title order={6} className={classes.title}>
          By Subtopic
        </Title>
        <CollapseIcon isOpened={subtopicOpened} />
        <Collapse in={subtopicOpened} className={classes.collapseWrapper}>
          {Object.values(subtopicPerformance).map(
            (perf: SubtopicPerformance) => (
              <Box key={perf.subtopic} className={classes.collapseItemWrapper}>
                <Text size="xs" c="dimmed">
                  {perf.subtopic}
                </Text>
                <Text className={classes.marks}>
                  {perf.marksGained} <span>/{perf.total} marks</span>
                </Text>
              </Box>
            )
          )}
        </Collapse>
      </Box>
    </div>
  );
}

function CollapseIcon({ isOpened }: { isOpened: boolean }) {
  return (
    <ActionIcon variant="filled" size="xs" color="darkblue" p={"sm"}>
      {isOpened ? (
        <ArrowUpwardRounded style={{ width: 14, height: 14 }} />
      ) : (
        <ArrowDownwardRounded style={{ width: 14, height: 14 }} />
      )}
    </ActionIcon>
  );
}
